import type { Identifier } from "ra-core";

import { osloDateKey } from "../misc/osloDate";
import {
  addWorkdays,
  daysBetween,
  osloHour,
  workdaysBetween,
} from "../misc/workdays";
import type { Deal, DealStage, TaskType, WorkItem } from "../types";
import { dealMonthlyValue, isDealOpen } from "./dealStatus";

// Neste steg og råtning (pipeline v2.1, kap. 7 og 8). Neste steg er vanlige
// oppgaver koblet til muligheten (work_items.deal_id). En mulighet har et
// neste steg når den har en åpen oppgave med dato.

export type NextStepState = "overdue" | "today" | "future" | "none";

export type DealTask = Pick<
  WorkItem,
  | "id"
  | "title"
  | "due_date"
  | "completed_at"
  | "created_at"
  | "assignee_id"
  | "company_id"
  | "deal_id"
  | "task_type"
>;

// Oppgavetypene som er neste steg. Revisit, anmeldelse og fakturering kommer
// fra Tapt og Vunnet (pakke 3 og 4).
export const NEXT_STEP_TYPES: TaskType[] = [
  "call",
  "meeting",
  "site_visit",
  "email",
  "task",
];

export const TASK_TYPES: TaskType[] = [
  ...NEXT_STEP_TYPES,
  "revisit",
  "review",
  "billing",
];

// Fullførte oppgaver av disse typene skriver kontaktlogg (kap. 7.1).
export const CONTACT_TASK_TYPES: TaskType[] = [
  "call",
  "meeting",
  "site_visit",
  "email",
];

// Den åpne oppgaven med tidligst dato. Oppgaver uten dato teller ikke som
// neste steg.
export const pickNextStep = <T extends DealTask>(
  tasks: T[] | undefined,
): T | undefined =>
  (tasks ?? [])
    .filter((t) => !t.completed_at && t.due_date)
    .sort(
      (a, b) =>
        (a.due_date ?? "").localeCompare(b.due_date ?? "") ||
        String(a.id).localeCompare(String(b.id)),
    )[0];

// Åpne oppgaver per mulighet, sortert med neste steg først.
export const groupTasksByDeal = <T extends DealTask>(
  tasks: T[] | undefined,
): Map<string, T[]> => {
  const byDeal = new Map<string, T[]>();
  for (const task of tasks ?? []) {
    if (task.deal_id == null || task.completed_at) continue;
    const key = String(task.deal_id);
    const list = byDeal.get(key) ?? [];
    list.push(task);
    byDeal.set(key, list);
  }
  for (const list of byDeal.values()) {
    list.sort(
      (a, b) =>
        (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999") ||
        String(a.id).localeCompare(String(b.id)),
    );
  }
  return byDeal;
};

export const nextStepState = (
  task: Pick<DealTask, "due_date"> | undefined,
  today: string = osloDateKey(),
): NextStepState => {
  if (!task?.due_date) return "none";
  if (task.due_date < today) return "overdue";
  if (task.due_date === today) return "today";
  return "future";
};

export const overdueDays = (
  task: Pick<DealTask, "due_date"> | undefined,
  today: string = osloDateKey(),
): number =>
  task?.due_date && task.due_date < today
    ? daysBetween(task.due_date, today)
    : 0;

const activityKey = (deal: Pick<Deal, "last_activity_at" | "created_at">) => {
  const at = deal.last_activity_at ?? deal.created_at;
  return at ? osloDateKey(new Date(at)) : undefined;
};

// Kalenderdager siden siste aktivitet («Stille i 9 dager»).
export const silentDays = (
  deal: Pick<Deal, "last_activity_at" | "created_at">,
  today: string = osloDateKey(),
): number => {
  const key = activityKey(deal);
  return key ? Math.max(0, daysBetween(key, today)) : 0;
};

// Råtner: åpen mulighet uten aktivitet i stadiets antall virkedager.
export const isDealRotting = (
  deal: Pick<
    Deal,
    "status" | "stage" | "last_activity_at" | "created_at" | "archived_at"
  >,
  dealStages: DealStage[],
  today: string = osloDateKey(),
): boolean => {
  if (!isDealOpen(deal) || deal.archived_at) return false;
  const stage = dealStages.find((s) => s.value === deal.stage);
  const limit = stage?.rot_after_workdays;
  if (!limit) return false;
  const key = activityKey(deal);
  if (!key) return false;
  return workdaysBetween(key, today) >= limit;
};

// Tilbudet utløper om 3 dager eller mindre (kap. 8.2).
export const OFFER_WARNING_DAYS = 3;

export const offerExpiresSoon = (
  deal: Pick<Deal, "offer_valid_until" | "status">,
  today: string = osloDateKey(),
): boolean => {
  if (!deal.offer_valid_until || !isDealOpen(deal)) return false;
  const days = daysBetween(today, deal.offer_valid_until);
  return days >= 0 && days <= OFFER_WARNING_DAYS;
};

const STATE_RANK: Record<NextStepState, number> = {
  overdue: 0,
  today: 1,
  none: 2,
  future: 3,
};

// Sortering etter behov for handling (kap. 8.3): forfalt → i dag → ingen
// neste steg → fremtidig (tidligst først). Ved likhet vises den nyeste først.
export const compareByActionNeed = (
  a: { deal: Pick<Deal, "created_at" | "id">; next?: DealTask },
  b: { deal: Pick<Deal, "created_at" | "id">; next?: DealTask },
  today: string = osloDateKey(),
): number => {
  const rank = (x: typeof a) => STATE_RANK[nextStepState(x.next, today)];
  return (
    rank(a) - rank(b) ||
    (a.next?.due_date ?? "").localeCompare(b.next?.due_date ?? "") ||
    (b.deal.created_at ?? "").localeCompare(a.deal.created_at ?? "") ||
    String(b.deal.id).localeCompare(String(a.deal.id))
  );
};

export type SuggestedNextStep = {
  title: string;
  type: TaskType;
  due_date: string;
};

// Svarfristen for nye leads (leadIntake.replyCutoffHour) når innstillingen
// mangler.
export const DEFAULT_REPLY_CUTOFF_HOUR = 14;

// Forslaget for stadiet (innstilling per stadium). Innboksen: samme dag hvis
// henvendelsen kom før kl. 14 (innstilling), ellers neste virkedag (kap. 7.1).
export const suggestNextStep = (
  stage: DealStage | undefined,
  options: {
    now?: Date;
    fallbackTitle?: string;
    replyCutoffHour?: number;
  } = {},
): SuggestedNextStep => {
  const now = options.now ?? new Date();
  const today = osloDateKey(now);
  const cutoff = options.replyCutoffHour ?? DEFAULT_REPLY_CUTOFF_HOUR;
  const offset = stage?.is_inbox
    ? osloHour(now) < cutoff
      ? 0
      : 1
    : (stage?.next_step_offset_workdays ?? 1);
  return {
    title: stage?.next_step_title || options.fallbackTitle || "",
    type: (stage?.next_step_type as TaskType | null | undefined) ?? "task",
    due_date: addWorkdays(today, offset),
  };
};

export type DealRequiredField = NonNullable<
  DealStage["required_fields"]
>[number];

export type DealGateContext = {
  tasks: DealTask[];
  hasOfferFile: boolean;
};

// Inngangskriteriene for stadiet som mangler (kap. 5.2). next_step og
// meeting_date fylles som oppgaver; resten er felt på muligheten.
export const missingRequiredFields = (
  deal: Pick<
    Deal,
    | "contact_ids"
    | "company_id"
    | "source"
    | "category"
    | "budget_range"
    | "one_time_value"
    | "monthly_value"
    | "amount"
    | "offer_valid_until"
  >,
  stage: DealStage | undefined,
  context: DealGateContext,
): DealRequiredField[] => {
  const open = context.tasks.filter((t) => !t.completed_at);
  const checks: Record<DealRequiredField, boolean> = {
    contact: (deal.contact_ids ?? []).length > 0,
    company: deal.company_id != null,
    source: !!deal.source,
    service: !!deal.category,
    budget: !!deal.budget_range?.trim(),
    value:
      Number(deal.one_time_value ?? deal.amount ?? 0) > 0 ||
      dealMonthlyValue(deal) > 0,
    next_step: open.some((t) => !!t.due_date),
    meeting_date: context.tasks.some(
      (t) =>
        (t.task_type === "meeting" || t.task_type === "site_visit") &&
        !!t.due_date,
    ),
    offer_file: context.hasOfferFile,
    offer_valid_until: !!deal.offer_valid_until,
  };
  return (stage?.required_fields ?? []).filter((field) => !checks[field]);
};

export const dealIdKey = (id: Identifier | null | undefined) =>
  id == null ? "" : String(id);
